/**
 * Thin fetch wrapper for calling the API.
 *
 * Centralises the base URL, the admin bearer token and JSON handling so screens
 * do not each re-implement them.
 */

import API_BASE_URL from '../config/api';

export const getAdminToken = () => localStorage.getItem('adminToken');

const buildHeaders = ({ token, isFormData }) => {
  const headers = {};

  // Let the browser set the multipart boundary itself
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const authToken = token === undefined ? getAdminToken() : token;
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  return headers;
};

/**
 * Call the API and return the parsed body.
 *
 * Resolves with the payload for both success and error responses, because the
 * API always answers with a { success, error } envelope and the screens read
 * `error` from it.
 */
export const request = async (path, { method = 'GET', body, token, headers = {} } = {}) => {
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: { ...buildHeaders({ token, isFormData }), ...headers },
    ...(body === undefined ? {} : { body: isFormData ? body : JSON.stringify(body) })
  });

  const text = await response.text();
  let payload = null;

  try {
    payload = text ? JSON.parse(text) : null;
  } catch (error) {
    payload = { success: false, error: 'Unexpected response from the server' };
  }

  if (payload && typeof payload === 'object' && payload.success === undefined) {
    payload.success = response.ok;
  }

  return payload || { success: response.ok };
};

export const get = (path, options) => request(path, { ...options, method: 'GET' });
export const post = (path, body, options) => request(path, { ...options, method: 'POST', body });
export const put = (path, body, options) => request(path, { ...options, method: 'PUT', body });
export const del = (path, options) => request(path, { ...options, method: 'DELETE' });

const apiClient = { request, get, post, put, del, getAdminToken };

export default apiClient;
